/**
 * list-filter-options.ts — opcje list wyboru w WIERSZU FILTRÓW list kartotek (faktury, umowy, pisma,
 * dokumenty handlowe i wewnętrzne) i sekcji kokpitu decyzji, których nie da się zbudować czystym
 * builderem kolumn (`buildCoreColumns`, `buildDocumentCoreRefColumns`), bo trzeba je wczytać:
 *  - pozycje SŁOWNIKÓW pól `select` rdzenia (pilność i sposób płatności faktur — pole ma
 *    `dictionaryCode`, a katalog pól nie niesie jego pozycji),
 *  - UŻYTKOWNICY tenanta dla kolumny „Opiekun" (`owner_user_id`, wartość filtra = id użytkownika).
 *
 * Jeden hook dla wszystkich list i sekcji (zamiast kopii w każdej stronie): wołający podaje katalog
 * pól, dostaje gotowy obiekt, który przekazuje do builderów kolumn. Zasady:
 *  - słowniki idą tym samym zapytaniem i kluczem cache co `useDictionary` (`fetchDictionaryDetail`,
 *    `dictionaryDetailKey`), więc nie dublują żądań z formularzami; trasa nie ma bramki uprawnień;
 *  - wartość opcji = kod pozycji (to, co serwer trzyma w kolumnie), etykieta wg `displayMode` słownika.
 *    Pozycje nieaktywne i ukryte też są na liście — dokument może jeszcze nieść starą wartość;
 *  - pola rysowane nadpisaniem kolumny albo osobnym builderem (`skipSlugs`, patrz
 *    `listFilterSkipSlugs`) nie pobierają słownika — ich opcje buduje własny kod (status i typ
 *    dokumentu faktur to zamknięte listy z kodu);
 *  - słownik, którego nie udało się wczytać, zostaje bez listy wyboru, jak dotąd (kolumna bez opcji);
 *    awaria inna niż brak prawa (403) i brak słownika (404) — też sukces bez szczegółów słownika —
 *    kończy się wpisem `console.error` także w produkcji, a 403 / 404 ostrzeżeniem w trybie
 *    deweloperskim; wpis jest jeden na parę (hak wołający, kod słownika). Słownik, który się
 *    wczytuje (zapytanie bez danych i bez błędu, także ponowne pobranie po błędzie), nie jest
 *    „niedostępny" — wołający pokazuje wtedy pustą komórkę filtra, a nie pole tekstowe;
 *  - użytkownicy z `GET /users` (jak `UserPicker`/`useUserLabels`), do 200 (górny clamp silnika list).
 *    Lista wymaga prawa podglądu użytkowników (`crm_core.users.view`) — bez niego zapytania w ogóle nie
 *    wysyłamy, a przy odmowie 403 mimo prawa w panelu (odmowy nie ponawiamy) albo przy błędzie opcji
 *    jest pusta, a kolumna „Opiekun" zostaje bez kontrolki filtra (pusta komórka, jak „Firma" bez
 *    firm), zamiast martwego pola. Tenant z ponad 200 użytkownikami zobaczy w filtrze pierwszych 200
 *    (znany limit, jak przy liście firm w `useCompanies`). Mapowanie użytkowników na opcje idzie w
 *    `select` zapytania: użytkownik bez nazwy i loginu (rozjazd kontraktu) to błąd zapytania — brak
 *    opcji i wpis `console.error` z jego id — a nie wyjątek, który wywróciłby całą listę. Każdy
 *    inny błąd pobrania poza odmową 403 też kończy się wpisem `console.error` (jeden na błąd i
 *    wołającego, niezależnie od liczby jednocześnie zamontowanych instancji haka). Kolejność opcji:
 *    alfabetycznie wg języka interfejsu (jak opcje relacji), liczona poza projekcją zapytania.
 *
 * Dwa wspólne haki niżej (`useUserFilterOptions`, `useDictionaryDetailsByCode`) woła też hak
 * opcji wiersza filtrów kartotek własnych
 * (`lib/custom-modules/use-module-list-filter-options.ts`) — jedna implementacja zamiast kopii.
 */

import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueries, type UseQueryResult } from '@tanstack/react-query';

import {
  DICTIONARY_DETAIL_STALE_TIME_MS,
  type DictionaryDetail,
  dictionaryDetailKey,
  fetchDictionaryDetail,
  formatDictionaryOptionLabel,
} from '@/hooks/useDictionary';
import { usePermission } from '@/hooks/usePermission';
import type {
  CoreSelectFilterOptions,
  ListFilterOption,
  SystemModuleFieldDef,
} from '@/lib/ext-list-columns';
import { isForbiddenError, isNotFoundError, retryUnlessForbidden } from '@/lib/query';
import { type UsersListResponse, useUsersList } from '@/lib/use-permissions';

/** Opcje wczytane dla list: listy wyboru pól `select` rdzenia wg sluga pola + użytkownicy (opiekun). */
export interface ListFilterOptions {
  /** Slug pola `select` rdzenia → pozycje jego słownika (patrz `buildCoreColumns({ selectOptions })`). */
  selectOptions: CoreSelectFilterOptions;
  /** Użytkownicy tenanta jako opcje filtra „Opiekun"; pusta = brak kontrolki filtra. */
  ownerOptions: ReadonlyArray<ListFilterOption>;
}

/** Brak opcji — wartość domyślna dla wołających, którzy ich nie wczytują (testy, niewpięte listy). */
export const EMPTY_LIST_FILTER_OPTIONS: ListFilterOptions = { selectOptions: {}, ownerOptions: [] };

/** Pusta lista opcji „Opiekun" — stabilna referencja, gdy użytkownicy się nie wczytali. */
const EMPTY_OWNER_OPTIONS: ReadonlyArray<ListFilterOption> = [];

/** Górny clamp silnika list dla `GET /users` (patrz `UsersListOptions.limit`). */
const OWNER_OPTIONS_LIMIT = 200;

/** Prawo, którego wymaga lista użytkowników po stronie API (deskryptor listy `users`). */
const USERS_VIEW_PERMISSION = 'crm_core.users.view';

/**
 * Sluga pola `select` rdzenia → kod słownika, dla pól, które trafią do kolumn listy i mają słownik, ale
 * nie mają własnych opcji w katalogu (te dostałyby w wierszu filtrów pustą komórkę). Pola poza listą
 * (`showInList: false`) pomijamy — nie ma kolumny, więc nie ma po co pobierać słownika. Pomijamy też
 * `skipSlugs`: pola rysowane nadpisaniem kolumny albo osobnym builderem — generyk ich nie rysuje, więc
 * opcji ze słownika nikt by nie użył.
 */
export function dictionaryCodesBySlug(
  fields: ReadonlyArray<SystemModuleFieldDef>,
  skipSlugs?: ReadonlySet<string>,
): Record<string, string> {
  const bySlug: Record<string, string> = {};
  for (const field of fields) {
    if (!field.isCore || !field.showInList || field.fieldType !== 'select') continue;
    if (skipSlugs?.has(field.slug)) continue;
    if ((field.options?.length ?? 0) > 0) continue;
    const code = field.dictionaryCode?.trim();
    if (code) bySlug[field.slug] = code;
  }
  return bySlug;
}

/**
 * Slugi pól, dla których strona albo zestaw sekcji NIE używa opcji ze słownika: klucze rejestru nadpisań
 * kolumn (`*_OVERRIDES` — nadpisanie zastępuje kolumnę generyczną i samo buduje opcje) oraz slugi
 * obsługiwane osobnym builderem (`*_EXCLUDE_SLUGS`). Wołaj RAZ na poziomie modułu — wynik jest stabilnym
 * argumentem `useListFilterOptions`.
 */
export function listFilterSkipSlugs(
  overrides: Readonly<Record<string, unknown>>,
  excludeSlugs: ReadonlySet<string>,
): ReadonlySet<string> {
  return new Set([...Object.keys(overrides), ...excludeSlugs]);
}

/**
 * Pozycje słownika jako opcje filtra: w kolejności `sortOrder` słownika (pozycje bez kolejności na
 * końcu, zachowując kolejność z serwera), wartość = kod, etykieta wg `displayMode` słownika.
 */
export function dictionaryFilterOptions(dictionary: DictionaryDetail): ListFilterOption[] {
  const ordered = dictionary.items
    .map((item, index) => ({ item, index }))
    .sort(
      (a, b) =>
        (a.item.sortOrder ?? Number.MAX_SAFE_INTEGER) -
          (b.item.sortOrder ?? Number.MAX_SAFE_INTEGER) || a.index - b.index,
    );
  return ordered.map(({ item }) => ({
    value: item.code,
    label: formatDictionaryOptionLabel(item, dictionary.displayMode),
    ...(item.color ? { color: item.color } : {}),
  }));
}

/**
 * Rozjazd kontraktu listy użytkowników (użytkownik bez nazwy i loginu). Osobna klasa, żeby w logu
 * i w testach dało się go odróżnić od porażki pobrania; hook loguje oba rodzaje, pomija tylko
 * odmowę 403.
 */
export class UserFilterOptionsError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'UserFilterOptionsError';
  }
}

/**
 * Opcje alfabetycznie po etykiecie wg języka interfejsu (`locale`, np. `i18n.language`; pusty =
 * domyślny przeglądarki) — ta sama kolejność co opcje relacji w filtrach kartoteki własnej. Nowa
 * lista; wejście bez zmian.
 */
export function sortFilterOptionsByLabel(
  options: ReadonlyArray<ListFilterOption>,
  locale: string,
): ListFilterOption[] {
  const collator = new Intl.Collator(locale === '' ? undefined : locale);
  return [...options].sort((a, b) => collator.compare(a.label, b.label));
}

/**
 * Użytkownicy jako opcje filtra „Opiekun", w kolejności odpowiedzi (kolejność wg języka interfejsu
 * nakłada hak — `sortFilterOptionsByLabel` — poza projekcją zapytania, żeby ta zostawała stabilna).
 * Etykieta jak w komórce „Opiekun" (etykiety odwołań do użytkowników — `resolveUsersLabels` w API):
 * nazwa wyświetlana, gdy pusta — login; NIGDY e-mail (decyzja właściciela 2026-10-09: ta sama osoba
 * wygląda w filtrze i w komórce tak samo). Login jest w bazie `NOT NULL` i trasa listy użytkowników
 * zawsze go zwraca — jego brak to rozjazd kontraktu, więc jawny błąd (`UserFilterOptionsError`)
 * zamiast cichego zastępstwa.
 */
export function userFilterOptions(
  users: ReadonlyArray<{ id: string; displayName: string | null; username?: string }>,
): ListFilterOption[] {
  return users.map((user) => {
    const label = user.displayName?.trim() || user.username?.trim();
    if (!label) {
      throw new UserFilterOptionsError(
        `list-filter-options: użytkownik ${user.id} bez nazwy i loginu`,
      );
    }
    return { value: user.id, label };
  });
}

/**
 * `select` zapytania o użytkowników: odpowiedź listy → opcje filtra „Opiekun". Funkcja modułowa
 * (stabilna referencja — TanStack liczy projekcję tylko przy zmianie danych); wyjątek mapowania staje
 * się błędem zapytania, a nie wyjątkiem w renderze.
 */
function selectOwnerOptions(response: UsersListResponse): ListFilterOption[] {
  return userFilterOptions(response.users);
}

/**
 * Odpowiedź o słownik bez jego szczegółów (sukces HTTP, ale `dictionary` puste) — rozjazd kontraktu
 * trasy. To nie jest „wczytywanie": zapytanie jest rozstrzygnięte, więc traktujemy je jak błąd
 * wczytania (z wpisem w logu), zamiast czekać bez końca na dane, które nie przyjdą.
 */
class DictionaryDetailMissingError extends Error {
  constructor() {
    super('odpowiedź bez szczegółów słownika');
    this.name = 'DictionaryDetailMissingError';
  }
}

// Jedna instancja (stabilna tożsamość błędu w wyniku `combine`, więc efekt logowania nie biegnie
// od nowa przy każdym przeliczeniu).
const DICTIONARY_DETAIL_MISSING_ERROR = new DictionaryDetailMissingError();

/**
 * Stan wczytania jednego słownika — same dane potrzebne hookowi: szczegóły albo błąd oraz status
 * zapytania (`pending` = jeszcze ani danych, ani błędu). `refetching` = trwa ponowne pobranie
 * po błędzie.
 */
interface DictionaryLoadState {
  detail: DictionaryDetail | undefined;
  error: Error | null;
  status: 'pending' | 'error' | 'success';
  refetching: boolean;
}

// Stabilna referencja (inaczej `combine` przeliczałby się co render): zbiera same dane zapytań.
function combineDictionaryStates(
  results: UseQueryResult<DictionaryDetail, Error>[],
): DictionaryLoadState[] {
  return results.map((result): DictionaryLoadState => {
    // Typ mówi, że sukces niesie szczegóły, ale odpowiedź trasy bywa pusta (`dictionary: null`) —
    // przeczytane osobno, żeby kompilator nie uznał tej gałęzi za nieosiągalną.
    const detail: DictionaryDetail | null | undefined = result.data;
    // Sukces bez szczegółów słownika to błąd wczytania, nie „czekanie".
    if (result.status === 'success' && !detail) {
      return {
        detail: undefined,
        error: DICTIONARY_DETAIL_MISSING_ERROR,
        status: 'error',
        refetching: result.isFetching,
      };
    }
    return {
      detail: detail ?? undefined,
      error: result.error,
      status: result.status,
      // Tylko ponowne pobranie po błędzie — zwykłe odświeżanie w tle nie zmienia wyniku `combine`.
      refetching: result.status === 'error' && result.isFetching,
    };
  });
}

// Strażnik niewczytanego słownika (deduplikowany po `${nazwa haka}:${kod słownika}`: dwa różne haki
// zgłaszają ten sam słownik osobno, bo każdy ma własny skutek dla użytkownika). Brak prawa (403)
// i brak słownika (404) to oczekiwane stany konfiguracji — ostrzeżenie tylko w trybie deweloperskim
// (`import.meta.env.DEV`); każda inna awaria (sieć, 5xx, odpowiedź bez szczegółów) kończy się
// `console.error` także w produkcji, jak błędy opcji relacji — bez wpisu kolumna zostawałaby bez
// listy wyboru i bez wyjaśnienia.
const reportedDictionaryLoadFailures = new Set<string>();
function reportDictionaryLoadFailure(
  code: string,
  error: Error,
  hookName: string,
  consequence: string,
): void {
  const expected = isForbiddenError(error) || isNotFoundError(error);
  if (expected && !import.meta.env.DEV) return;
  const key = `${hookName}:${code}`;
  if (reportedDictionaryLoadFailures.has(key)) return;
  reportedDictionaryLoadFailures.add(key);
  const message = `[${hookName}] Nie wczytano słownika „${code}" (${error.message}) — ${consequence}`;
  if (expected) console.warn(message);
  else console.error(message);
}

/** Stan wczytania słowników o podanych kodach (`useDictionaryDetailsByCode`). */
export interface DictionaryDetailsByCode {
  /** Słowniki, które się wczytały (kod → słownik). */
  loaded: ReadonlyMap<string, DictionaryDetail>;
  /**
   * Kody słowników, które jeszcze się wczytują: zapytanie bez danych i bez błędu (status `pending`)
   * albo ponowne pobranie po błędzie. Słownik z rozstrzygniętym błędem (też sukces bez szczegółów
   * słownika) nie jest ani wczytany, ani wczytywany.
   */
  pending: ReadonlySet<string>;
}

/**
 * Pozycje słowników o podanych kodach, wczytane tym samym zapytaniem i kluczem cache co
 * `useDictionary` (jedno żądanie na kod, bez dublowania z formularzami). Wynik rozdziela słowniki
 * wczytane (`loaded`) od jeszcze wczytywanych (`pending`: status zapytania `pending` albo ponowne
 * pobranie po błędzie); słownik niedostępny nie jest żadnym z nich i jest zgłaszany w logu (raz na
 * parę `hookName` + kod; `console.error`, a dla 403 / 404 ostrzeżenie tylko w trybie deweloperskim —
 * patrz `reportDictionaryLoadFailure`; `hookName` + `consequence` opisują wołającego i skutek dla
 * użytkownika). Odpowiedź bez szczegółów słownika jest takim samym błędem wczytania (a nie
 * wiecznym „wczytywaniem”). `codes` musi mieć stabilną tożsamość (memoizacja po stronie
 * wołającego); wynik ma ją stabilną, dopóki nie zmienią się dane.
 */
export function useDictionaryDetailsByCode(
  codes: ReadonlyArray<string>,
  hookName: string,
  consequence: string,
): DictionaryDetailsByCode {
  const states = useQueries({
    queries: codes.map((code) => ({
      queryKey: dictionaryDetailKey(code),
      queryFn: () => fetchDictionaryDetail(code),
      staleTime: DICTIONARY_DETAIL_STALE_TIME_MS,
    })),
    combine: combineDictionaryStates,
  });

  // Zgłoś (raz na parę haka i kodu) słownik, którego nie udało się wczytać. Nieudane odświeżenie
  // w tle przy wczytanych wcześniej pozycjach nie jest tym przypadkiem — lista wyboru działa.
  useEffect(() => {
    codes.forEach((code, index) => {
      const state = states[index];
      if (state?.error && !state.detail) {
        reportDictionaryLoadFailure(code, state.error, hookName, consequence);
      }
    });
  }, [codes, states, hookName, consequence]);

  return useMemo(() => {
    const loaded = new Map<string, DictionaryDetail>();
    const pending = new Set<string>();
    codes.forEach((code, index) => {
      const state = states[index];
      if (state?.detail) loaded.set(code, state.detail);
      else if (state === undefined || state.status === 'pending' || state.refetching) {
        pending.add(code);
      }
    });
    return { loaded, pending };
  }, [codes, states]);
}

/** Argumenty `useUserFilterOptions`. */
export interface UseUserFilterOptionsArgs {
  /**
   * Czy wołający w ogóle potrzebuje użytkowników (np. lista kartoteki ma pole „użytkownik");
   * `false` = zapytania nie ma. Brak prawa podglądu użytkowników wyłącza je niezależnie od flagi.
   */
  enabled: boolean;
  /** Nazwa wołającego haka — prefiks wpisu `console.error`. */
  hookName: string;
  /** Nazwa filtra w komunikacie („Opiekun", „użytkownik"). */
  filterLabel: string;
}

// Strażnik błędów opcji użytkowników (deduplikowany po `${nazwa haka}:${filtr}:${komunikat}`): wiele
// instancji haka jednocześnie (np. sekcje kokpitu decyzji) widzi tę samą awarię tego samego
// zapytania — jeden wpis na awarię i wołającego, jak przy opcjach relacji. Inny komunikat to inna
// awaria, więc jest zgłaszany osobno.
const reportedUserFilterFailures = new Set<string>();
function reportUserFilterFailure(hookName: string, filterLabel: string, error: Error): void {
  const key = `${hookName}:${filterLabel}:${error.message}`;
  if (reportedUserFilterFailures.has(key)) return;
  reportedUserFilterFailures.add(key);
  console.error(
    `[${hookName}] Nie zbudowano opcji filtra „${filterLabel}" — ` +
      `kolumna zostaje bez listy wyboru:`,
    error.message,
  );
}

/**
 * Użytkownicy tenanta jako opcje filtra (`userFilterOptions`: nazwa, a bez niej login; nigdy
 * e-mail), do 200, alfabetycznie wg języka interfejsu (`sortFilterOptionsByLabel`; sortowanie
 * poza `select` zapytania, żeby projekcja zostawała stabilna). Bez prawa podglądu użytkowników
 * zapytania nie ma (zawsze kończyłoby się 403, a bez danych TanStack ponawia pobranie przy każdym
 * montażu listy); odmowa 403 mimo prawa w panelu (serwer decyduje ostatecznie) nie jest ponawiana
 * ani logowana. Mapowanie na opcje idzie w `select`, więc zły wiersz to błąd zapytania (brak
 * opcji), nie wyjątek w renderze. KAŻDY błąd poza 403 — rozjazd kontraktu listy, awaria sieci,
 * 5xx — zgłasza `console.error` (jeden wpis na błąd i wołającego, patrz
 * `reportUserFilterFailure`). Wynik pusty = brak kontrolki filtra.
 */
export function useUserFilterOptions({
  enabled,
  hookName,
  filterLabel,
}: UseUserFilterOptionsArgs): ReadonlyArray<ListFilterOption> {
  const { i18n } = useTranslation();
  const locale = i18n.language;
  const canViewUsers = usePermission(USERS_VIEW_PERMISSION).allowed;
  const usersQ = useUsersList({
    limit: OWNER_OPTIONS_LIMIT,
    retry: retryUnlessForbidden,
    select: selectOwnerOptions,
    enabled: canViewUsers && enabled,
  });
  const error = usersQ.error;
  useEffect(() => {
    if (error === null || isForbiddenError(error)) return;
    reportUserFilterFailure(hookName, filterLabel, error);
  }, [error, hookName, filterLabel]);
  const options = usersQ.data;
  return useMemo(
    () => (options === undefined ? EMPTY_OWNER_OPTIONS : sortFilterOptionsByLabel(options, locale)),
    [options, locale],
  );
}

/**
 * Wczytuje opcje filtra wiersza filtrów dla listy kartoteki / sekcji kokpitu decyzji (patrz nagłówek
 * pliku). `fields` — katalog pól modułu (`useSystemModuleFields`), `undefined` do czasu wczytania.
 * `skipSlugs` — pola rysowane nadpisaniem albo osobnym builderem (patrz `listFilterSkipSlugs`; stała
 * modułowa, nie tworzona w renderze). Wynik ma stabilną tożsamość, dopóki nie zmienią się dane — można
 * go wpisać do zależności `useMemo` kolumn.
 */
export function useListFilterOptions(
  fields: ReadonlyArray<SystemModuleFieldDef> | undefined,
  skipSlugs?: ReadonlySet<string>,
): ListFilterOptions {
  const codeBySlug = useMemo(
    () => dictionaryCodesBySlug(fields ?? [], skipSlugs),
    [fields, skipSlugs],
  );
  const codes = useMemo(() => [...new Set(Object.values(codeBySlug))].sort(), [codeBySlug]);

  const dictionaries = useDictionaryDetailsByCode(
    codes,
    'useListFilterOptions',
    'kolumny z tym słownikiem zostają bez listy wyboru w wierszu filtrów.',
  );

  const selectOptions = useMemo<CoreSelectFilterOptions>(() => {
    const byCode = new Map<string, ListFilterOption[]>();
    for (const code of codes) {
      const detail = dictionaries.loaded.get(code);
      if (detail) byCode.set(code, dictionaryFilterOptions(detail));
    }
    const bySlug: Record<string, ReadonlyArray<ListFilterOption>> = {};
    for (const [slug, code] of Object.entries(codeBySlug)) {
      const options = byCode.get(code);
      if (options && options.length > 0) bySlug[slug] = options;
    }
    return bySlug;
  }, [codes, codeBySlug, dictionaries]);

  // Opiekun: użytkownicy tenanta (prawo podglądu, 403, log błędów — patrz `useUserFilterOptions`).
  const ownerOptions = useUserFilterOptions({
    enabled: true,
    hookName: 'useListFilterOptions',
    filterLabel: 'Opiekun',
  });

  return useMemo(() => ({ selectOptions, ownerOptions }), [selectOptions, ownerOptions]);
}
